import type { SpanHeatmapSeriesConfig } from "types/dashboard.types"
import type { SpanDatum } from "types/spans.types"

interface BuildSpanDataResult {
  spanData: SpanDatum[]
  maxPct: number
}

// `{ [_field]: { [spanId]: count } }`, as `only_value_summary` returns it.
export type CountsByField = Record<string, Record<string, number>>

/**
 * One `SpanDatum` per span in physical order, shared by the map and the ribbon
 * so they scale colors off the same numbers. Counts become a share of the
 * field's total, which makes them independent of how the series was sampled.
 */
export const buildSpanDataFromCounts = (
  countsByField: CountsByField,
  series: SpanHeatmapSeriesConfig[],
  orderedSpanIds: number[],
): BuildSpanDataResult => {
  const aarSeries = series.find((s) => s.dataKey === "aar")
  const dlrSeries = series.find((s) => s.dataKey === "dlr")

  const totalOf = (field?: string): number => {
    if (!field) return 0
    return Object.values(countsByField[field] ?? {}).reduce((sum, c) => sum + c, 0)
  }
  const aarTotal = totalOf(aarSeries?.field) || 1
  const dlrTotal = totalOf(dlrSeries?.field) || 1

  // Order comes from the powerline, which lists its spans along the route.
  const spanList = orderedSpanIds.map((id) => ({ spanId: id, key: String(id) }))

  const spanData: SpanDatum[] = spanList.map(({ spanId, key }, index) => {
    const aarCount = aarSeries ? (countsByField[aarSeries.field]?.[key] ?? 0) : 0
    const dlrCount = dlrSeries ? (countsByField[dlrSeries.field]?.[key] ?? 0) : 0
    return {
      spanIndex: index + 1,
      spanId,
      aar: { count: aarCount, pct: (aarCount / aarTotal) * 100 },
      dlr: { count: dlrCount, pct: (dlrCount / dlrTotal) * 100 },
    }
  })

  // Floored, so a line with no counts at all still divides.
  const maxPct = Math.max(...spanData.flatMap((d) => [d.aar.pct, d.dlr.pct]), 0.001)

  return { spanData, maxPct }
}
